import { FormattedMessage } from 'react-intl';
import { Service, useDataService } from 'services/data.service';
import { ExportRequest, useExportRequest } from 'hooks/useExportRequest';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useContext } from 'react';
import { format } from 'date-fns';
import { Button } from 'components/Button';
import { Tooltip } from 'components/Tooltip';

export interface ReportDownloadProps {
  service: Service;
  tableName: string;
  measuresGroup: string;
  requestDecorator?: (exportRequest: ExportRequest) => ExportRequest;
  isArticleVisibility?: boolean;
  isProReport?: boolean;
  disabled?: boolean;
}

export function ReportDownload({
  service,
  tableName,
  measuresGroup,
  requestDecorator = (dr: ExportRequest) => dr,
  isArticleVisibility = false,
  isProReport = false,
  disabled = false,
}: ReportDownloadProps) {
  const exportData = useExportRequest();
  const { initiateExport } = useDataService(service);
  const tenantConfig = useContext(TenantConfigContext);

  const createTimeLabel = () => {
    if (isArticleVisibility && !exportData.creationTimeFilter) {
      if (!tenantConfig.currentDataVersion) return '';
      return format(tenantConfig.currentDataVersion, 'yyyyMMdd');
    }

    const timeFilterForFileName = isArticleVisibility ? exportData.creationTimeFilter : exportData?.timeFilter;
    return timeFilterForFileName?.fromDate && timeFilterForFileName?.toDate
      ? `${timeFilterForFileName?.fromDate.replace('-', '_')}-${timeFilterForFileName.toDate}`
      : '';
  };

  const timeLabel = createTimeLabel();

  const fileName = exportData.timeFilter
    ? `${tenantConfig.name.replace(/\./g, ' ')}_${tableName}_table_${timeLabel}`
    : '';

  return (
    <div>
      <Tooltip tooltipContent={<FormattedMessage id="insights.report.download" defaultMessage="Download" />}>
        <Button
          icon="Download"
          size="lg"
          id={`download-${tableName}${isProReport ? '-pro' : ''}${isArticleVisibility ? '-av' : ''}`}
          data-testid={`download-${tableName}${isProReport ? '-pro' : ''}${isArticleVisibility ? '-av' : ''}`}
          disabled={disabled}
          onClick={() => {
            if (disabled) {
              return;
            }
            initiateExport(measuresGroup, requestDecorator(exportData)).then(resp => {
              if (resp.data) {
                const fileURL = window.URL.createObjectURL(resp.data);
                // Setting various property values
                const alink = document.createElement('a');
                alink.href = fileURL;
                alink.download = fileName;
                alink.click();
                window.URL.revokeObjectURL(fileURL);
              }
            });
          }}
        />
      </Tooltip>
    </div>
  );
}
